import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, LinearProgress, Rating, alpha, DialogContent, DialogTitle, Dialog, DialogActions, IconButton, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { faBoltLightning, faDroplet, faFire, faChargingStation, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ColoredBarWithText from "src/components/ColoredBarWithText";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    position: "relative",
  },
  barContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  barLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  barWrapper: {
    position: "relative",
    width: "100%",
  },
  bar: {
    height: 28, // högre staplar
    borderRadius: 6,
  },
  barText: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
  },
}));

function AverageComparisonCard({ consumption, averageData, className, isApartmentOverview = false, ...rest }) {
  const classes = useStyles();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  if (!consumption || !averageData) return null;

  const rows = [
    {
      field: 'coldwater',
      text: 'Kallvatten',
      unit: 'kbm',
      visible: consumption.coldWaterSensorsExists,
      icon: faDroplet,
      iconColor: "#0289d1"
    },
    {
      field: 'hotwater',
      text: 'Varmvatten',
      unit: 'kbm',
      visible: consumption.hotWaterSensorsExists,
      icon: faDroplet,
      iconColor: "#ff0000"
    },
    {
      field: 'electricity',
      text: 'El',
      unit: 'kWh',
      visible: consumption.electicitySensorsExists,
      icon: faBoltLightning,
      iconColor: "#FFA500"
    },
    {
      field: 'heatenergy',
      text: 'Värmeenergi',
      unit: 'kWh',
      visible: consumption.heatEnergySensorsExists,
      icon: faFire,
      iconColor: "#964B00"
    },
    {
      field: 'evCharging',
      text: 'Laddning elbil',
      unit: 'kWh',
      visible: consumption.evChargingSensorsExists,
      icon: faChargingStation,
      iconColor: "#008000"
    }
  ];

  // Summera totalsiffror
  let totalUser = 0;
  let totalAvg = 0;

  rows.forEach(row => {
    if (row.visible) {
      const userVal = consumption.periodSummary?.[row.field]?.value ?? 0;
      const avgVal = averageData.periodSummary?.[row.field]?.value ?? 0;
      totalUser += userVal;
      totalAvg += avgVal;
    }
  });

  // Räkna skillnad i procent
  let diffPercent = 0;
  if (totalAvg > 0) {
    diffPercent = ((totalUser - totalAvg) / totalAvg) * 100;
  }
  const diffPercentRounded = diffPercent?.toFixed(2);

  // Bestäm antal stjärnor (1–3)
  let stars = 1;
  if (diffPercentRounded <= -20) {
    stars = 3;   // Mycket bättre än snitt
  } else if (diffPercentRounded < 0) {
    stars = 2;   // Lite bättre än snitt
  } else {
    stars = 1;   // Sämre än snitt
  }

  return (
    <Card className={clsx(classes.root, className)} sx={{boxShadow: isApartmentOverview ? "none !important" : "", padding: isApartmentOverview ? "16px !important" : ""}} {...rest}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="textSecondary">
          Jämfört med andra i samma fastighet
        </Typography>

        <IconButton onClick={() => setInfoDialogOpen(true)}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </IconButton>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={2}>
        <Typography variant="h4" color="primary">
          {diffPercentRounded > 0 ? `+${diffPercentRounded}%` : `${diffPercentRounded}%`}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" mb={2}>
        <Rating value={stars} max={3} readOnly />
      </Box>

      {rows.map(row => row.visible && (
        <Box key={row.field} className={classes.barContainer}>
          <div className={classes.barLabel}>
            <Typography variant="body2">
              <FontAwesomeIcon icon={row.icon} color={row.iconColor} style={{ marginRight: 8 }} />
              {row.text}
            </Typography>
          </div>

          <Typography variant="caption" color="textSecondary">Genomsnitt ({averageData.periodSummary[row.field].value} {row.unit})</Typography>
          {/*<Box className={classes.barWrapper}>
            <LinearProgress
              variant="determinate"
              value={(averageData.periodSummary[row.field].value /
                Math.max(consumption.periodSummary[row.field].value, averageData.periodSummary[row.field].value)) * 100}
              classes={{ root: classes.bar }}
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: row.iconColor,
                },
                backgroundColor: "#FFF"
              }}
            />
            <Typography 
              className={classes.barText}
            >
              {averageData.periodSummary[row.field].value} {row.unit}
            </Typography>
          </Box>*/}
          <Box className={classes.barWrapper}>
          <ColoredBarWithText
            value={averageData.periodSummary[row.field].value}
            maxValue={Math.max(
              consumption.periodSummary[row.field].value,
              averageData.periodSummary[row.field].value
            )}
            color={row.iconColor}
            unit={row.unit}
          />
          </Box>

          <Typography variant="caption" color="textSecondary">{isApartmentOverview ? "Denna lägenhet" : "Ditt hem"} ({consumption.periodSummary[row.field].value} {row.unit})</Typography>
          {/*<Box className={classes.barWrapper}>
            <LinearProgress
              variant="determinate"
              value={(consumption.periodSummary[row.field].value /
                Math.max(consumption.periodSummary[row.field].value, averageData.periodSummary[row.field].value)) * 100}
              classes={{ root: classes.bar }}
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: alpha(row.iconColor, 0.8),
                },
                backgroundColor: "#FFF"
              }}
            />
            <Typography 
              className={classes.barText}
            >
              {consumption.periodSummary[row.field].value} {row.unit}
            </Typography>
          </Box>*/}
          <Box className={classes.barWrapper}>
          <ColoredBarWithText
            value={consumption.periodSummary[row.field].value}
            maxValue={Math.max(
              consumption.periodSummary[row.field].value,
              averageData.periodSummary[row.field].value
            )}
            color={alpha(row.iconColor, 0.7)}
            unit={row.unit}
          />
          </Box>
        </Box>
      ))}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
        <DialogTitle>Om jämförelsen</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Denna jämförelse visar hur {isApartmentOverview ? "denna lägenhet" : "ditt hem"} presterar jämfört med genomsnittet. 
            Alla siffror räknas automatiskt baserat på insamlade mätvärden från {isApartmentOverview ? "denna lägenhet" : "ditt hem"} och jämförelsedata.
          </Typography>
          <Typography>
            Stjärnorna visar hur mycket bättre eller sämre {isApartmentOverview ? "denna lägenhet" : "ditt hem"} är i förhållande till snittet.
            Ju fler stjärnor, desto bättre.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">Stäng</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

AverageComparisonCard.propTypes = {
  className: PropTypes.string,
  consumption: PropTypes.object.isRequired,
  averageData: PropTypes.object.isRequired,
};

export default AverageComparisonCard;
